/*
 * ContractEditablePreview.jsx (Punto 20 — field-only editing)
 *
 * Anteprima full-text del contratto in tempo reale, con editing limitato
 * SOLO ai campi input dell'utente (chip contenteditable). Il resto del
 * testo legale è bloccato.
 *
 * Il backend serve l'HTML avvolgendo ogni placeholder editabile in
 * <span class="cg-field" data-field="…" contenteditable="true">. Il resto
 * del container ha contentEditable={false}.
 *
 * Su input in un chip → richiamiamo `onFieldChange(name, value)` che
 * aggiorna lo state nel modal parent (bidirectional binding).
 *
 * Nessun dual engine: dato che l'edit finisce sempre nello state, il PDF
 * viene generato dal template ReportLab ufficiale usando i valori aggiornati.
 */
import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";

export default function ContractEditablePreview({ payload, onFieldChange }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const containerRef = useRef(null);
  const skipReloadRef = useRef(false);

  // Fetch HTML from server. Debounce = 350ms.
  // NOTE: we deliberately skip the *next* fetch that would fire immediately
  // after the user's own edit (to avoid stealing focus / caret from the chip
  // they're typing into). The refetch resumes on the next external prop change.
  useEffect(() => {
    if (skipReloadRef.current) {
      skipReloadRef.current = false;
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const r = await api.post("/contract-preview", payload);
        if (!cancelled) setHtml(r.data?.html || "");
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Errore anteprima");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(payload)]);

  // Delegated input handler: any chip edit updates form state.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      const chip = e.target?.closest?.("span.cg-field");
      if (!chip) return;
      const field = chip.getAttribute("data-field");
      if (!field) return;
      const value = chip.textContent || "";
      skipReloadRef.current = true; // don't refetch server HTML for our own edit
      if (onFieldChange) onFieldChange(field, value);
    };
    el.addEventListener("input", handler);
    return () => el.removeEventListener("input", handler);
  }, [onFieldChange]);

  return (
    <div
      className="bg-white rounded-md border border-slate-200 shadow-sm text-slate-800 min-h-[400px]"
      data-testid="cg-preview-document"
    >
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2 text-[11px]" data-testid="cg-preview-header">
        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
        <span className="text-slate-600">
          Anteprima <strong>editabile solo nei campi utente</strong> (evidenziati in giallo). Il testo legale è bloccato.
        </span>
      </div>
      {loading && !html && (
        <div className="p-6 text-sm text-slate-500 italic">Rigenerazione anteprima…</div>
      )}
      {error && (
        <div className="p-6 text-sm text-red-700 bg-red-50 border-t border-red-200">{error}</div>
      )}
      {html && (
        <div
          ref={containerRef}
          contentEditable={false}
          className="contract-preview-body [&_.cg-field]:bg-yellow-100 [&_.cg-field]:px-1 [&_.cg-field]:rounded-sm [&_.cg-field]:border [&_.cg-field]:border-yellow-300 [&_.cg-field]:outline-none [&_.cg-field:focus]:bg-yellow-200 [&_.cg-field:focus]:border-yellow-500 [&_.cg-field:hover]:bg-yellow-200 [&_p]:my-1"
          dangerouslySetInnerHTML={{ __html: html }}
          data-testid="cg-preview-html"
        />
      )}
    </div>
  );
}
